(ns ui.animation-editor.actions
  "The action handlers of the Replicant story template editor. The state
   of the editor lives under `:animation-editor` in the store:

   `:status`       `:loading`, `:ready`, `:missing` or `:error`
   `:entity`       the story template as the user edits it
   `:tab`          the active tab (see `ui.animation-editor.model/tabs`)
   `:stage-ready?` the composition and the background image are loaded
   `:saving?`      the save command is running
   `:saved?`       saved, the gallery opens in a moment"
  (:require [parts.replicant.query :as query]
            [ui.animation-editor.model :as model]
            [ui.animation-editor.stage :as stage]
            [ui.animation-editor.transport :as transport]
            [ui.app-frame :as app-frame]
            [ui.error-alert :as error-alert]
            [ui.gallery.link :as gallery-link]))

(def gallery-url
  "Where the editor goes after the save: the gallery of
   `ui.gallery.link`."
  (gallery-link/gallery-href))

(defn show-error!
  [store message]
  (error-alert/show-error {:error message
                           :store store}))

(defn receive!
  "Applies the `response` of the query to the editor state, unless the
   page was loaded again in the meantime (`load-id`)."
  [store load-id response]
  (when (= load-id (get-in @store [:animation-editor :load-id]))
    (let [result (:result response)]
      (swap! store
             update
             :animation-editor
             (fn [editor]
               (cond
                 (not (:success? response))
                 (assoc editor :status :error)

                 (:animation-editor/missing? result)
                 (assoc editor :status :missing)

                 :else
                 (assoc editor
                        :status :ready
                        :entity (model/initial-entity (:animation-editor/entity result)))))))))

(defn init!
  "The `:on-load` of the page: starts with the defaults and loads the
   story template (or the background image) the URL names."
  [{:keys [ui/store ui/event-handler action] :as w}]
  (let [[_ location] action
        q (model/query-of-location (:location/query-params location))
        load-id (random-uuid)]
    ;; the navbar and the sidebar need the session:
    (when-not (query/available? @store app-frame/user-session-query)
      (event-handler {} [[:data/query app-frame/user-session-query]]))
    (swap! store
           assoc
           :animation-editor
           {:status (if q :loading :ready)
            :load-id load-id
            :tab (first model/tabs)
            :entity model/default-entity})
    (when q
      (-> (transport/query! w q)
          (.then (fn [response]
                   (receive! store load-id response)))
          (.catch (fn [error]
                    (js/console.error "animation editor query failed:" error)
                    (when (= load-id (get-in @store [:animation-editor :load-id]))
                      (swap! store assoc-in [:animation-editor :status] :error))))))))

(defn stage-loaded!
  "The iframe loaded the template page: loads the composition, applies
   the entity and starts the animation."
  [{:keys [ui/store]}]
  (let [started? (stage/init!
                   (fn [start-animation]
                     (stage/prepare! (get-in @store [:animation-editor :entity])
                                     (fn []
                                       (start-animation)
                                       (swap! store assoc-in [:animation-editor :stage-ready?] true)))))]
    (when-not started?
      (js/console.error "animation editor: the template page did not load" stage/template-path)
      (swap! store assoc-in [:animation-editor :stage-failed?] true))))

(defn update-entity!
  "Applies `f` to the entity and shows the result on the stage."
  [store f & args]
  (swap! store update-in [:animation-editor :entity] (fn [entity] (apply f entity args)))
  (stage/set-attributes! (get-in @store [:animation-editor :entity])))

(defn set-text!
  [{:keys [ui/store action]}]
  (let [[_ layer text] action]
    (update-entity! store model/set-text layer text)))

(defn set-font!
  [{:keys [ui/store action]}]
  (let [[_ layer attr value] action]
    (update-entity! store model/set-font layer attr value)))

(defn set-font-slider!
  [{:keys [ui/store action]}]
  (let [[_ layer attr s] action]
    (when-let [value (model/parse-slider-value attr s)]
      (update-entity! store model/set-font layer attr value))))

(defn set-symbol!
  "Chooses a preset and plays the animation from the start, so the user
   sees it."
  [{:keys [ui/store action]}]
  (let [[_ layer symbol-name] action]
    (update-entity! store model/set-symbol layer symbol-name)
    (stage/restart!)))

(defn set-background-color!
  [{:keys [ui/store action]}]
  (let [[_ color] action]
    (swap! store update-in [:animation-editor :entity] model/set-background-color color)
    (stage/set-background-color! color)))

(defn set-tab!
  [{:keys [ui/store action]}]
  (let [[_ tab] action]
    (swap! store assoc-in [:animation-editor :tab] tab)))

(defn saved!
  [store]
  (swap! store update :animation-editor assoc :saving? false :saved? true)
  (js/setTimeout (fn []
                   (js/location.assign gallery-url))
                 2500))

(defn save-failed!
  [store message]
  (swap! store assoc-in [:animation-editor :saving?] false)
  (show-error! store
               (if (string? message)
                 message
                 "The story template could not be saved. Please try again.")))

(defn save!
  "Sends the entity to `:command/animation-editor-save` and opens the
   gallery afterwards, where the rendered video shows up."
  [{:keys [ui/store] :as w}]
  (let [entity (get-in @store [:animation-editor :entity])]
    (if (model/savable? entity)
      (do
        (swap! store assoc-in [:animation-editor :saving?] true)
        (-> (transport/command! w
                                {:command/kind :command/animation-editor-save
                                 :command/data {:animation-editor/entity entity}})
            (.then (fn [response]
                     (if (:success? response)
                       (saved! store)
                       (save-failed! store (get-in response [:result :error])))))
            (.catch (fn [error]
                      (js/console.error "animation editor save failed:" error)
                      (save-failed! store nil)))))
      (show-error! store "Please open the editor from a story image, a story template needs a background image."))))

(defn handler
  [kind f]
  {:ui.action/kind kind
   :ui.action/handler f})

(def handlers
  [(handler :animation-editor/init init!)
   (handler :animation-editor/stage-loaded stage-loaded!)
   (handler :animation-editor/set-text set-text!)
   (handler :animation-editor/set-font set-font!)
   (handler :animation-editor/set-font-slider set-font-slider!)
   (handler :animation-editor/set-symbol set-symbol!)
   (handler :animation-editor/set-background-color set-background-color!)
   (handler :animation-editor/set-tab set-tab!)
   (handler :animation-editor/save save!)])
